<template>
    <div>
        <div class="pp-filters mb-4">
            <v-text-field v-model="filters.search" placeholder="Buscar empresa, processo, vara ou juiz" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="pp-search" @input="debouncedLoad"></v-text-field>
            <v-select v-model="filters.uf" :items="ufItems" label="UF" dense outlined hide-details clearable :menu-props="{ offsetY: true }" class="pp-select" @change="load"></v-select>
            <v-select v-model="filters.status" :items="statusItems" label="Etapa" dense outlined hide-details clearable :menu-props="{ offsetY: true }" class="pp-select pp-select--wide" @change="load"></v-select>
            <v-select v-model="filters.type" :items="typeItems" label="Tipo" dense outlined hide-details clearable :menu-props="{ offsetY: true }" class="pp-select" @change="load"></v-select>
            <v-select v-model="filters.min_amount" :items="amountItems" label="Passivo" dense outlined hide-details :menu-props="{ offsetY: true }" class="pp-select" @change="load"></v-select>
            <v-select v-model="filters.owner" :items="ownerItems" label="Dono" dense outlined hide-details clearable :menu-props="{ offsetY: true }" class="pp-select pp-select--wide" @change="load"></v-select>
            <v-checkbox v-model="filters.only_pending" label="Só PP pendente" dense hide-details class="mt-0" @change="load"></v-checkbox>
            <v-checkbox v-model="filters.include_below" label="Incluir passivo baixo" dense hide-details class="mt-0" @change="load"></v-checkbox>
            <span class="pp-actions">
                <v-btn outlined small :disabled="!total" @click="exportRows"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
                <v-btn color="primary" depressed small :loading="pdfLoading" @click="exportPdf"><v-icon left small>mdi-file-pdf-box</v-icon>Material de sexta (PDF)</v-btn>
            </span>
        </div>

        <div v-if="selectedIds.length" class="pp-bulk mb-3">
            <span class="ui-small"><strong>{{ selectedIds.length }}</strong> {{ selectedIds.length === 1 ? "caso selecionado" : "casos selecionados" }}</span>
            <v-btn small color="primary" depressed @click="tripPicker = true"><v-icon left small>mdi-map-marker-plus-outline</v-icon>Planejar visita</v-btn>
            <v-btn small text @click="selectedIds = []">Limpar seleção</v-btn>
        </div>

        <div v-if="loading" class="ui-muted ui-small py-6 text-center">Carregando...</div>
        <ui-empty v-else-if="!groups.length" text="Nenhum caso pendente neste filtro. Os casos entram aqui quando são triados como acompanhar e saem quando a AJ é nomeada." />

        <section v-for="group in groups" :key="group.uf" class="pp-group">
            <header class="pp-group__header">
                <h2 class="ui-section-title">{{ group.uf }}</h2>
                <span class="ui-small ui-muted">
                    {{ plural(group.cases.length, "caso", "casos") }} acima do corte · {{ formatMoneyShort(group.total_amount) }}
                    · {{ group.meetings }} com agenda<!-- Desativado (29/10/2026): dono deixou de ser obrigatório. <span v-if="group.without_owner"> · {{ group.without_owner }} sem dono</span> -->
                </span>
            </header>
            <ui-table-card v-if="group.cases.length">
                <v-data-table :headers="visibleHeaders" :items="group.cases" disable-sort hide-default-footer :items-per-page="-1" item-key="id" class="ui-clickable-rows" @click:row="open">
                    <template v-slot:[`item.select`]="{ item }">
                        <div @click.stop><v-simple-checkbox :value="selectedIds.includes(item.id)" :ripple="false" @input="toggle(item.id)"></v-simple-checkbox></div>
                    </template>
                    <template v-slot:[`item.company_name`]="{ item }">
                        <div class="ui-cell-main" style="min-width: 170px;">{{ item.company_name }}</div>
                        <router-link v-for="trip in item.trips || []" :key="trip.id" :to="`/prospeccao/rota/${trip.id}`" class="pp-trip"
                            :title="`No roteiro ${trip.name}${trip.start_date ? `, saída ${formatFeedDate(trip.start_date)}` : ''}`" @click.native.stop>
                            <v-icon x-small>mdi-map-marker-path</v-icon><span class="pp-trip__name">{{ trip.name }}</span>
                        </router-link>
                        <div class="ui-cell-sub"><span class="ui-mono">{{ item.process_number }}</span> · {{ item.type_label }}</div>
                        <div v-if="compact" class="ui-cell-sub">ajuizada {{ formatFeedDate(item.filed_at) }}<span v-if="item.owner"> · dono: {{ item.owner.name }}</span></div>
                    </template>
                    <template v-slot:[`item.amount`]="{ item }">
                        <span class="ui-nowrap">{{ formatMoneyShort(item.amount) }}</span>
                        <div v-if="item.amount_source === 'valor_causa'" class="ui-cell-sub">valor da causa</div>
                    </template>
                    <template v-slot:[`item.court_name`]="{ item }">
                        <div class="ui-cell-main" style="min-width: 170px;">{{ item.city || item.comarca || "—" }} · {{ item.court_name || "—" }}</div>
                        <div class="ui-cell-sub">{{ item.judge_name || "juiz não informado" }}</div>
                    </template>
                    <template v-slot:[`item.filed_at`]="{ item }"><span class="ui-nowrap">{{ formatFeedDate(item.filed_at) }}</span></template>
                    <template v-slot:[`item.stages`]="{ item }">
                        <div class="pp-stages">
                            <span class="pp-stage" :class="{ 'pp-stage--done': item.stages.onepage }">onepage</span>
                            <span class="pp-stage" :class="{ 'pp-stage--done': item.stages.agenda }">agenda</span>
                            <span class="pp-stage" :class="stageClass(item.pp_status, item.pp_administrator)">{{ item.pp_status === "not_applicable" ? "PP n/a" : "PP" }}</span>
                            <span class="pp-stage" :class="stageClass(item.aj_status, item.aj_administrator)">AJ</span>
                        </div>
                        <div class="ui-cell-sub">
                            <ui-badge set="prospect-pipeline" :status="item.pipeline_status" />
                            <span v-if="item.pp_administrator"> PP: {{ item.pp_administrator.name }}</span>
                        </div>
                    </template>
                    <template v-slot:[`item.last_bej`]="{ item }">
                        <div v-if="compact" class="ui-label mb-0">BeJ</div>
                        <template v-if="item.last_bej_activity">
                            <div class="ui-cell-main pp-move">{{ item.last_bej_activity.description }}</div>
                            <div class="ui-cell-sub">{{ formatShort(item.last_bej_activity.occurred_at) }}<span v-if="item.last_bej_activity.admin_name"> · {{ item.last_bej_activity.admin_name }}</span> · {{ daysSince(item.last_bej_activity.occurred_at) }} d</div>
                        </template>
                        <!-- Desativado (29/10/2026): "sem dono · N d" em vermelho; dono deixou de ser obrigatório. -->
                        <span v-else class="ui-small ui-muted">—</span>
                        <template v-if="compact && item.last_court_activity">
                            <div class="ui-label mb-0 mt-1">Autos</div>
                            <div class="ui-cell-main pp-move">{{ item.last_court_activity.description }}</div>
                            <div class="ui-cell-sub">{{ formatShort(item.last_court_activity.occurred_at) }}</div>
                        </template>
                    </template>
                    <template v-slot:[`item.last_court`]="{ item }">
                        <template v-if="item.last_court_activity">
                            <div class="ui-cell-main pp-move">{{ item.last_court_activity.description }}</div>
                            <div class="ui-cell-sub">{{ formatShort(item.last_court_activity.occurred_at) }}</div>
                        </template>
                        <span v-else class="ui-muted">—</span>
                    </template>
                    <template v-slot:[`item.owner`]="{ item }"><span :class="item.owner ? '' : 'ui-muted'">{{ item.owner ? item.owner.name : "—" }}</span></template>
                </v-data-table>
            </ui-table-card>
            <p v-if="group.hidden_below.length" class="ui-small ui-muted mt-2">
                {{ plural(group.hidden_below.length, "caso abaixo do corte oculto", "casos abaixo do corte ocultos") }} ({{ group.hidden_below.join(", ") }}).
            </p>
        </section>

        <prospect-trip-picker v-model="tripPicker" :case-ids="selectedIds" :uf="selectedUf" @done="onPlanned" />
    </div>
</template>

<script>
// Aba "Em acompanhamento" da tela de Casos: a lista da reuniao de sexta
// (AJ pendente e caso aberto), por UF e em ordem de passivo.
import axios from "axios";
import ProspectTripPicker from "@/components/prospect/ProspectTripPicker.vue";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import {
    APPOINTMENT_LABELS, PENDING_STATUS_ITEMS, TYPE_ITEMS, daysSince, formatFeedDate, formatMoneyShort, notifyError, openPdf,
} from "@/components/prospect/prospect.util";

const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];

export default {
    name: "ProspectCasePendingBoard",

    components: { ProspectTripPicker },

    data: () => ({
        groups: [],
        selectedIds: [],
        tripPicker: false,
        pdfLoading: false,
        total: 0,
        loading: false,
        team: [],
        timer: null,
        filters: { search: "", uf: null, status: null, type: null, min_amount: 0, owner: null, only_pending: false, include_below: false },
        ufItems: UFS,
        statusItems: PENDING_STATUS_ITEMS,
        typeItems: TYPE_ITEMS,
        amountItems: [
            { value: 0, text: "Todos" },
            { value: 3000000, text: "≥ R$ 3 mi" },
            { value: 10000000, text: "≥ R$ 10 mi" },
            { value: 50000000, text: "≥ R$ 50 mi" },
        ],
        headers: [
            { text: "", value: "select", width: 36 },
            { text: "Empresa · processo", value: "company_name" },
            { text: "Passivo", value: "amount", align: "end", width: 120 },
            { text: "Município · vara · juiz", value: "court_name" },
            { text: "Ajuiz.", value: "filed_at", width: 90 },
            { text: "Etapa BeJ", value: "stages", width: 190 },
            { text: "Últ. mov. BeJ", value: "last_bej", width: 190 },
            { text: "Últ. mov. autos", value: "last_court", width: 170 },
            { text: "Dono", value: "owner", width: 110 },
        ],
    }),

    computed: {
        // Tela estreita: ajuizamento e dono vao para a empresa; autos para o ultimo movimento.
        compact() {
            return this.$vuetify.breakpoint.width < 1400;
        },
        visibleHeaders() {
            if (!this.compact) return this.headers;
            return this.headers
                .filter((header) => !["filed_at", "last_court", "owner"].includes(header.value))
                .map((header) => (header.value === "last_bej" ? { ...header, text: "Últimos movimentos" } : header));
        },
        // UF comum dos selecionados (o roteiro novo nasce nela).
        selectedUf() {
            const ufs = new Set();
            this.groups.forEach((group) => group.cases.forEach((item) => {
                if (this.selectedIds.includes(item.id)) ufs.add(group.uf);
            }));
            return ufs.size === 1 ? [...ufs][0] : null;
        },
        ownerItems() {
            return [{ value: "none", text: "Sem dono" }, ...this.team.map((person) => ({ value: person.id, text: person.name }))];
        },
    },

    created() {
        this.load();
        this.loadTeam();
    },

    methods: {
        daysSince,
        formatFeedDate,
        formatMoneyShort,

        plural(count, one, many) {
            return `${count} ${count === 1 ? one : many}`;
        },

        formatShort(value) {
            const text = String(value || "");
            return text ? `${text.slice(8, 10)}/${text.slice(5, 7)}` : "";
        },

        stageClass(status, administrator) {
            if (status !== "appointed") return status === "not_applicable" ? "pp-stage--na" : "";
            return administrator && administrator.is_house ? "pp-stage--house" : "pp-stage--other";
        },

        params() {
            const params = {};
            Object.entries(this.filters).forEach(([key, value]) => {
                if (value !== null && value !== "" && value !== false && value !== 0) params[key] = value;
            });
            return params;
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/prospect/pending", { params: this.params() });
                this.groups = result.data.content.groups;
                this.total = result.data.content.total;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        async loadTeam() {
            try {
                const result = await axios.get("/api/admin/prospect/team");
                this.team = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        toggle(id) {
            this.selectedIds = this.selectedIds.includes(id) ? this.selectedIds.filter((item) => item !== id) : [...this.selectedIds, id];
        },

        onPlanned() {
            this.selectedIds = [];
            this.load();
        },

        debouncedLoad() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.load(), 350);
        },

        open(item) {
            this.$router.push({ path: `/prospeccao/casos/${item.id}`, query: { from: "acompanhamento" } });
        },

        // Mesmo filtro da tela, no tema BeJ.
        async exportPdf() {
            this.pdfLoading = true;
            try {
                await openPdf(axios, "/api/admin/prospect/reports/pending.pdf", this.params());
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.pdfLoading = false;
            }
        },

        exportRows() {
            const rows = [];
            this.groups.forEach((group) => group.cases.forEach((item) => rows.push({
                UF: group.uf,
                Empresa: item.company_name,
                Processo: item.process_number,
                Tipo: item.type_label,
                Passivo: item.amount,
                "Origem do valor": item.amount_source === "passivo" ? "passivo" : "valor da causa",
                Município: item.city || item.comarca || "",
                Vara: item.court_name || "",
                Juiz: item.judge_name || "",
                Ajuizamento: formatFeedDate(item.filed_at),
                PP: item.pp_administrator ? item.pp_administrator.name : APPOINTMENT_LABELS[item.pp_status],
                AJ: item.aj_administrator ? item.aj_administrator.name : APPOINTMENT_LABELS[item.aj_status],
                Etapa: item.pipeline_status_label,
                "Últ. mov. BeJ": item.last_bej_activity ? `${this.formatShort(item.last_bej_activity.occurred_at)} ${item.last_bej_activity.description}` : "",
                "Últ. mov. autos": item.last_court_activity ? `${this.formatShort(item.last_court_activity.occurred_at)} ${item.last_court_activity.description}` : "",
                Dono: item.owner ? item.owner.name : "",
            })));
            downloadXlsx(rows, "pendentes-de-nomeacao");
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/ui/tokens";

.pp-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}

.pp-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: auto;
}

.pp-bulk {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 8px 12px;
    border-radius: $ui-radius-sm;
    background: $ui-accent-soft;
}

.pp-trip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    max-width: 210px;
    white-space: nowrap;
    margin-top: 3px;
    padding: 0 7px;
    font-size: 11px;
    border-radius: 999px;
    color: #5b21b6;
    background: #f5f3ff;
    text-decoration: none;
}

.pp-trip__name {
    overflow: hidden;
    text-overflow: ellipsis;
}

.pp-search {
    flex: 1 1 260px;
    max-width: 340px;
}

.pp-select {
    flex: 0 0 120px;
}

.pp-select--wide {
    flex-basis: 180px;
}

.pp-group {
    margin-bottom: 28px;
}

.pp-group__header {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-bottom: 8px;
}

.pp-move {
    max-width: 190px;
    white-space: normal;
}

.pp-stages {
    display: flex;
    gap: 4px;
    margin-bottom: 4px;
}

.pp-stage {
    padding: 1px 7px;
    font-size: 11px;
    border: 1px solid $ui-border;
    border-radius: 999px;
    color: $ui-subtle;
    background: $ui-bg;
}

.pp-stage--done {
    border-color: transparent;
    background: nth(map-get($ui-tones, primary), 1);
    color: nth(map-get($ui-tones, primary), 2);
}

.pp-stage--house {
    border-color: transparent;
    background: nth(map-get($ui-tones, success), 1);
    color: nth(map-get($ui-tones, success), 2);
}

.pp-stage--other {
    border-color: transparent;
    background: nth(map-get($ui-tones, danger), 1);
    color: nth(map-get($ui-tones, danger), 2);
}

.pp-stage--na {
    text-decoration: line-through;
}

.pp-alert {
    color: nth(map-get($ui-tones, danger), 2);
}
</style>
